'use client';

import { createContext, use } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDropzone } from 'react-dropzone';
import { tv } from 'tailwind-variants';

import { faCloudArrowUp } from '@icons/classic/regular';
import { Body } from '../typography/Body';
import { Label } from '../typography/Label';
import { clsxm } from '../utils';
import { Button } from './Button';
import { FlexContainer } from './FlexContainer';

import type { ReactNode } from 'react';
import type { DropEvent, DropzoneOptions, FileRejection } from 'react-dropzone';
import type { VariantProps } from 'tailwind-variants';

/**
 * Helper function to render file size in human-readable format
 */
function renderBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)}${units[unitIndex]}`;
}

// Context for sharing dropzone state with child components
interface DropzoneContextValue {
  files?: File[];
  accept?: DropzoneOptions['accept'];
  maxSize?: DropzoneOptions['maxSize'];
  minSize?: DropzoneOptions['minSize'];
  maxFiles?: DropzoneOptions['maxFiles'];
}

const DropzoneContext = createContext<DropzoneContextValue>({});

const dropzone = tv({
  base: `
    relative
    flex
    h-auto
    w-full
    flex-col
    overflow-hidden
    p-8
    transition-all
  `,
  variants: {
    isDragActive: {
      true: `
        ring-1
        ring-primary
        outline-none
      `,
    },
  },
});

export type DropzoneProps = Omit<DropzoneOptions, 'onDrop'> & {
  id?: string;
  files?: File[];
  className?: string;
  onDrop?: (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => void;
  children?: ReactNode;
} & VariantProps<typeof dropzone>;

function Dropzone({
  id,
  accept,
  maxFiles = 1,
  maxSize,
  minSize,
  onDrop,
  onError,
  disabled,
  files,
  className,
  children,
  ...props
}: DropzoneProps): React.ReactElement {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxFiles,
    maxSize,
    minSize,
    onError,
    disabled,
    onDrop: (acceptedFiles, fileRejections, event) => {
      if (fileRejections.length > 0) {
        const message = fileRejections.at(0)?.errors.at(0)?.message;
        onError?.(new Error(message));
        return;
      }
      onDrop?.(acceptedFiles, fileRejections, event);
    },
    ...props,
  });

  return (
    <DropzoneContext.Provider key={JSON.stringify(files)} value={{ files, accept, maxSize, minSize, maxFiles }}>
      <Button
        className={dropzone({ isDragActive, className })}
        data-slot="dropzone"
        disabled={disabled}
        id={id}
        type="button"
        variant="secondary"
        {...getRootProps()}
      >
        <input {...getInputProps()} disabled={disabled} />
        {children}
      </Button>
    </DropzoneContext.Provider>
  );
}

export type DropzoneContentProps = {
  children?: ReactNode;
  className?: string;
};

const MAX_LABEL_ITEMS = 3;

function DropzoneContent({ children, className }: DropzoneContentProps): React.ReactElement | null {
  const { files } = use(DropzoneContext);

  if (!files || files.length === 0) {
    return null;
  }

  if (children) {
    return <>{children}</>;
  }

  return (
    <FlexContainer
      className={className}
      data-slot="dropzone-content"
      direction="column"
      items="center"
      justify="center"
    >
      <FlexContainer
        className="size-8 rounded-md bg-background-secondary text-content-secondary"
        items="center"
        justify="center"
      >
        <FontAwesomeIcon icon={faCloudArrowUp} size="sm" />
      </FlexContainer>
      <Label className="my-2 w-full truncate text-content-primary" size="sm" weight="medium">
        {files.length > MAX_LABEL_ITEMS
          ? `${new Intl.ListFormat('en').format(
              files.slice(0, MAX_LABEL_ITEMS).map((file) => file.name)
            )} and ${files.length - MAX_LABEL_ITEMS} more`
          : new Intl.ListFormat('en').format(files.map((file) => file.name))}
      </Label>
      <Body className="w-full text-wrap text-content-secondary" size="xs">
        Drag and drop or click to replace
      </Body>
    </FlexContainer>
  );
}

export type DropzoneEmptyStateProps = {
  children?: ReactNode;
  className?: string;
};

function DropzoneEmptyState({ children, className }: DropzoneEmptyStateProps): React.ReactElement | null {
  const { files, accept, maxSize, minSize } = use(DropzoneContext);

  if (files && files.length > 0) {
    return null;
  }

  if (children) {
    return <>{children}</>;
  }

  let caption = '';
  if (accept) {
    caption += 'Accepts ';
    caption += new Intl.ListFormat('en').format(Object.keys(accept));
  }
  if (minSize && maxSize) {
    caption += ` between ${renderBytes(minSize)} and ${renderBytes(maxSize)}`;
  } else if (minSize) {
    caption += ` at least ${renderBytes(minSize)}`;
  } else if (maxSize) {
    caption += ` less than ${renderBytes(maxSize)}`;
  }

  return (
    <FlexContainer
      className={clsxm(className, 'gap-1 whitespace-normal')}
      data-slot="dropzone-empty-state"
      direction="column"
      items="center"
      justify="center"
    >
      <FlexContainer
        className="size-8 rounded-md bg-background-secondary text-content-secondary mb-4"
        items="center"
        justify="center"
      >
        <FontAwesomeIcon icon={faCloudArrowUp} size="sm" />
      </FlexContainer>
      <Body className="w-full text-content-secondary" weight="medium">
        Drag and drop or click to upload
      </Body>
      {caption && <Body className="text-content-secondary">{caption}.</Body>}
    </FlexContainer>
  );
}

export { dropzone, Dropzone, DropzoneContent, DropzoneEmptyState };
